import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/public-layout";

const PROSE =
  "prose prose-gray max-w-none " +
  "prose-headings:font-semibold prose-headings:text-gray-900 " +
  "prose-h1:text-3xl sm:prose-h1:text-4xl prose-h1:tracking-tight " +
  "prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3 " +
  "prose-p:text-gray-700 prose-li:text-gray-700 " +
  "prose-a:text-primary prose-a:no-underline hover:prose-a:underline";

export function PrivacyPage() {
  const canonicalUrl = `${window.location.origin}/privacy`;

  return (
    <PublicLayout>
      <Helmet>
        <title>Privacy Policy · DealerDog</title>
        <meta
          name="description"
          content="DealerDog Privacy Policy: how TrueCore Capital, LLC d/b/a DealerDog collects, uses, and shares information."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Privacy Policy · DealerDog" />
        <meta
          property="og:description"
          content="How DealerDog collects, uses, and shares information."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className={PROSE}>
          <h1>DealerDog Privacy Policy</h1>
          <p>
            <strong>Effective Date:</strong> October 30, 2026
          </p>

          <h2>1. Introduction</h2>
          <p>
            DealerDog is a platform operated by TrueCore Capital, LLC, doing business as DealerDog
            (“DealerDog,” “TrueCore Capital,” “we,” “us,” or “our”).
          </p>
          <p>
            We respect the privacy of dealers, business customers, financing applicants, and other
            individuals who interact with our website and services.
          </p>
          <p>
            This Privacy Policy explains the types of information we collect, how we use and disclose
            that information, and how individuals may contact us regarding their personal information.
          </p>
          <p>
            This policy applies to the DealerDog website, dealer registration, financing submissions,
            and related services.
          </p>

          <h2>2. Information We Collect</h2>
          <p>Depending on how you interact with DealerDog, we may collect the following information.</p>

          <h3>Dealer and Business Information</h3>
          <ul>
            <li>Business name, address, and contact information</li>
            <li>Dealer representative names and contact details</li>
            <li>Business registration and identification information</li>
            <li>Account credentials and platform activity</li>
            <li>Information regarding equipment, inventory, and sales transactions</li>
          </ul>

          <h3>Financing Applicant Information</h3>
          <ul>
            <li>Applicant names and contact information</li>
            <li>Business ownership and organizational information</li>
            <li>Financial statements, bank statements, and tax documents</li>
            <li>Credit-related information and financing history</li>
            <li>Personal information concerning business owners and guarantors</li>
            <li>Equipment details, invoices, and purchase documentation</li>
            <li>
              Government-issued identification and other documents submitted in connection with
              financing requests
            </li>
          </ul>

          <h3>Technical and Website Information</h3>
          <p>
            We may collect information such as IP addresses, browser types, device information,
            referring websites, and interactions with our platform.
          </p>
          <p>
            Some information may be collected automatically through cookies and similar technologies.
          </p>

          <h2>3. How We Use Information</h2>
          <p>We may use information to:</p>
          <ul>
            <li>Create and manage dealer accounts.</li>
            <li>Process and evaluate financing inquiries and applications.</li>
            <li>Communicate with dealers, applicants, and other transaction participants.</li>
            <li>Facilitate financing transactions and related documentation.</li>
            <li>Verify identities and help prevent fraud.</li>
            <li>Maintain, secure, and improve our platform.</li>
            <li>Provide customer support.</li>
            <li>Maintain business and transaction records.</li>
            <li>Comply with applicable laws, regulations, and legal obligations.</li>
          </ul>
          <p>
            We may also use business contact information to communicate about DealerDog services,
            subject to applicable law and available communication preferences.
          </p>

          <h2>4. Sharing and Disclosure of Information</h2>
          <p>
            We may disclose information when reasonably necessary to provide our services, including
            to:
          </p>

          <h3>Financing and Underwriting Partners</h3>
          <p>
            We may share financing applications, financial records, and supporting documentation with
            financial institutions, funding sources, underwriting partners, and other parties involved
            in evaluating or completing a requested financing transaction.
          </p>

          <h3>Dealers and Transaction Participants</h3>
          <p>
            Information may be shared with participating dealers, applicants, vendors, equipment
            sellers, and other authorized transaction participants as appropriate for the transaction.
          </p>

          <h3>Service Providers</h3>
          <p>
            We may use third-party providers for website hosting, document management, communications,
            customer relationship management, security, and other operational services.
          </p>

          <h3>Legal and Regulatory Purposes</h3>
          <p>
            We may disclose information when required by law, legal process, regulatory obligations, or
            to protect our rights, property, and security.
          </p>
          <p>
            We do not represent that every financing application will be submitted to every available
            financing partner.
          </p>

          <h2>5. Credit Information and Financing Applications</h2>
          <p>
            Certain financing transactions may require credit reports, financial records, identity
            verification, or additional underwriting documentation.
          </p>
          <p>
            The collection and use of credit-related information are subject to applicable laws and the
            authorizations and disclosures associated with the relevant financing application.
          </p>
          <p>
            Submitting information through DealerDog does not guarantee financing approval, funding, or
            any particular financing terms.
          </p>

          <h2>6. Information Submitted by Dealers</h2>
          <p>
            Dealers using DealerDog may submit information about customers, business owners,
            guarantors, and other individuals.
          </p>
          <p>
            Dealers are responsible for obtaining any notices, permissions, and authorizations required
            for their collection and submission of information.
          </p>
          <p>
            DealerDog may use submitted information to facilitate the financing services requested
            through the platform, consistent with applicable law and relevant agreements.
          </p>

          <h2>7. Cookies and Similar Technologies</h2>
          <p>
            DealerDog may use cookies and similar technologies to support website functionality,
            maintain user sessions, improve security, understand website performance, and support
            permitted analytics activities.
          </p>
          <p>The technologies used may change as the platform evolves.</p>
          <p>
            Users may be able to manage certain cookies through their browser settings. Disabling
            essential cookies may affect website functionality.
          </p>

          <h2>8. Information Security</h2>
          <p>
            We take reasonable measures intended to protect information against unauthorized access,
            disclosure, alteration, or destruction.
          </p>
          <p>
            However, no internet-based system or electronic storage method can be guaranteed to be
            completely secure.
          </p>
          <p>
            Users should protect their account credentials and notify us if they suspect unauthorized
            access.
          </p>

          <h2>9. Information Retention</h2>
          <p>
            We retain information for periods reasonably necessary to provide our services, administer
            transactions, maintain business records, resolve disputes, and comply with applicable legal
            and regulatory requirements.
          </p>
          <p>
            Retention periods may vary depending on the type of information, transaction status,
            contractual obligations, and applicable law.
          </p>
          <p>
            When information is no longer required, we may delete, anonymize, or otherwise dispose of
            it in accordance with applicable requirements.
          </p>

          <h2>10. Your Privacy Choices and Rights</h2>
          <p>
            Depending on applicable law and the nature of the information, individuals may have rights
            to request access to, correction of, or deletion of certain personal information.
          </p>
          <p>
            Some requests may be limited by legal obligations, transaction recordkeeping requirements,
            or other applicable exceptions.
          </p>
          <p>
            To submit a privacy inquiry or request, contact us using the information below.
          </p>
          <p>
            We may need to verify your identity or authority before fulfilling certain requests.
          </p>

          <h2>11. Third-Party Websites and Services</h2>
          <p>
            DealerDog may link to or integrate with third-party websites, applications, and services.
          </p>
          <p>
            Those third parties may maintain their own privacy policies and information-handling
            practices.
          </p>
          <p>
            We encourage users to review applicable third-party privacy notices when interacting with
            those services.
          </p>

          <h2>12. Children&apos;s Privacy</h2>
          <p>
            DealerDog is intended for business and commercial financing activities and is not directed
            toward children under 13.
          </p>
          <p>
            We do not knowingly solicit personal information directly from children under 13 through
            the platform.
          </p>

          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our services,
            information practices, or applicable requirements.
          </p>
          <p>
            When we update the policy, we will revise the effective date displayed above.
          </p>
          <p>Additional notice may be provided when required by applicable law.</p>

          <h2>14. Contact Information</h2>
          <p>
            For questions about this Privacy Policy or our information practices, please contact:
          </p>
          <p>
            TrueCore Capital, LLC
            <br />
            Doing Business As DealerDog
            <br />
            28245 Ave Crocker
            <br />
            Valencia, CA 91355
          </p>
          <p>
            Email:{" "}
            <a href="mailto:support@truecorecapital.com">support@truecorecapital.com</a>
          </p>
        </div>
      </article>
    </PublicLayout>
  );
}
